import { useState, useEffect } from "react";
import { X } from 'lucide-react';
import axios from "../../lib/axios";

interface ReactivateOtpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userData: any;
}

export default function ReactivateOtpModal({ isOpen, onClose, onSuccess, userData }: ReactivateOtpModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<"email" | "whatsapp">("email");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [resendLoading, setResendLoading] = useState(false);
  const [maskedRecipient, setMaskedRecipient] = useState("");

  useEffect(() => {
    if (otpSent && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [timeLeft, otpSent]);

  useEffect(() => {
    if (isOpen && userData) {
      // Set masked recipient based on selected method
      if (selectedMethod === "email") {
        const email = userData.email;
        const parts = email.split("@");
        const maskedName = parts[0].slice(0, 2) + "*".repeat(parts[0].length - 2);
        setMaskedRecipient(maskedName + "@" + parts[1]);
      } else {
        // Mask phone number (assuming phone exists)
        const phone = userData.phone || "+1234567890";
        const cleaned = phone.replace(/\D/g, "");
        if (cleaned.length > 4) {
          setMaskedRecipient(cleaned.slice(0, 2) + "*".repeat(cleaned.length - 4) + cleaned.slice(-2));
        } else {
          setMaskedRecipient(cleaned);
        }
      }
    }
  }, [isOpen, userData, selectedMethod]);

  const handleSendOtp = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post("/send-reactivation-otp", {
        type: selectedMethod,
      });

      if (response.data.success) {
        setOtpSent(true);
        setTimeLeft(120); // Reset timer to 2 minutes
        setMaskedRecipient(response.data.recipient || maskedRecipient);
      } else {
        setError(response.data.message || "Failed to send OTP");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    setVerifying(true);
    setError("");

    try {
      const response = await axios.post("/verify-reactivation-otp", {
        otp: otpCode,
      });

      if (response.data.success) {
        onSuccess();
      } else {
        setError(response.data.message || "Invalid OTP");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError("");
    setOtp(["", "", "", "", "", ""]);

    try {
      const response = await axios.post("/send-reactivation-otp", {
        type: selectedMethod,
      });

      if (response.data.success) {
        setTimeLeft(120); // Reset timer to 2 minutes
        setMaskedRecipient(response.data.recipient || maskedRecipient);
      } else {
        setError(response.data.message || "Failed to resend OTP");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // allow only numbers
    const cleaned = value.replace(/\D/g, "");

    // Handle paste of full OTP
    if (cleaned.length > 1) {
      const otpArray = cleaned.slice(0, 6).split("");

      const newOtp = [...otp];

      otpArray.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });

      setOtp(newOtp);

      // focus last filled input
      const nextIndex = Math.min(index + otpArray.length, 5);
      const nextInput = document.getElementById(
        `otp-${nextIndex}`
      ) as HTMLInputElement;

      if (nextInput) nextInput.focus();

      return;
    }

    const newOtp = [...otp];
    newOtp[index] = cleaned;
    setOtp(newOtp);

    // Auto-focus next input
    if (cleaned && index < 5) {
      const nextInput = document.getElementById(
        `otp-${index + 1}`
      ) as HTMLInputElement;

      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement;
      if (prevInput) prevInput.focus();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }; 

  const handleClose = () => {
    if (!verifying && !loading) {
      onClose();
      // Reset state
      setOtpSent(false);
      setOtp(["", "", "", "", "", ""]);
      setError("");
      setTimeLeft(120);
      setSelectedMethod("email");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="fixed inset-0 bg-gray-900/50 dark:bg-gray-900/80" onClick={handleClose}></div>
        
        <div className="relative bg-white dark:bg-[#0d1828] border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={verifying || loading}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-[#40bfb9]/10 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-[#40bfb9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Reactivate Your Account
            </h3>
            {!otpSent ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose how you'd like to receive your verification code
              </p>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enter the verification code sent to your {selectedMethod === 'email' ? 'email' : 'WhatsApp'}
              </p>
            )}
          </div>

          {!otpSent ? (
            /* Method Selection */
            <div className="space-y-4">
              <div className="space-y-3">
                <button
                  onClick={() => setSelectedMethod("email")}
                  className={`w-full text-left p-4 rounded-lg border-[1.5px] transition-colors ${
                    selectedMethod === "email"
                      ? "border-[#40bfb9] bg-[#40bfb9]/5"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">Email</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{userData?.email}</p>
                    </div>
                    {selectedMethod === "email" && (
                      <div className="w-5 h-5 bg-[#40bfb9] rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => setSelectedMethod("whatsapp")}
                  className={`w-full text-left p-4 rounded-lg border-[1.5px] transition-colors ${
                    selectedMethod === "whatsapp"
                      ? "border-[#40bfb9] bg-[#40bfb9]/5"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.16 3.072c.099.198 1.997 3.045 4.838 4.268.676.291 1.203.465 1.614.596.676.223 1.292.191 1.777.116.542-.084 1.662-.679 1.898-1.335.236-.656.236-1.218.173-1.335-.064-.149-.232-.223-.49-.372z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">WhatsApp</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {userData?.phone || "Available"}
                      </p>
                    </div>
                    {selectedMethod === "whatsapp" && (
                      <div className="w-5 h-5 bg-[#40bfb9] rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              </div>

              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                  {error}
                </div>
              )}

              <button
                onClick={handleSendOtp}
                disabled={loading}
                className="w-full flex items-center justify-center px-6 py-3 text-sm font-medium text-white transition rounded-lg bg-[#40bfb9] hover:bg-[#36a9a3] shadow-sm disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Verification Code"}
              </button>
            </div>
          ) : (
            /* OTP Verification */
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Enter the 6-digit code sent to {maskedRecipient}
                </p>
              </div>

              {/* OTP Input */}
              <div className="flex justify-center flex-wrap gap-2 sm:gap-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-10 h-10 sm:w-12 sm:h-12 text-center text-lg md:text-xl font-semibold border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1828] text-gray-900 dark:text-white outline-none transition-all focus:border-[#40bfb9] focus:ring-2 focus:ring-[#40bfb9]/20"
                  />
                ))}
              </div>

              {/* Timer */}
              <div className="text-center mt-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Resend Otp in {formatTime(timeLeft)}
                </p>
              </div>

              {/* Resend */}
              <div className="text-center">
                <button
                  onClick={handleResendOtp}
                  disabled={resendLoading || timeLeft > 0}
                  className="text-sm text-[#40bfb9] hover:text-[#36a9a3] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {resendLoading ? "Sending..." : timeLeft > 0 ? "Resend code" : "Resend code"}
                </button>
              </div>

              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 mt-2">
                <button
                  onClick={() => {
                    setOtpSent(false);
                    setOtp(["", "", "", "", "", ""]);
                    setError("");
                  }}
                  disabled={verifying}
                  className="flex-1 px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleVerifyOtp}
                  disabled={verifying || otp.join("").length !== 6}
                  className="flex-1 flex items-center justify-center px-6 py-2.5 text-sm font-medium text-white transition-colors rounded-lg bg-[#40bfb9] hover:bg-[#36a9a3] shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {verifying ? "Verifying..." : "Verify Code"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
